import { format, isValid, parse } from "date-fns";

export type TimeOfDay = { hours: number; minutes: number; seconds?: number };

/** Order of day, month and year in numeric dates for a locale. */
export type DateOrder = "dmy" | "mdy" | "ymd";

const REFERENCE_DATE = new Date(2006, 10, 22);

export const getLocaleDateOrder = (locale?: string): DateOrder => {
  try {
    const parts = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" })
      .formatToParts(REFERENCE_DATE)
      .map((part) => part.type)
      .filter((type) => type === "day" || type === "month" || type === "year");
    if (parts[0] === "year") return "ymd";
    if (parts[0] === "month") return "mdy";
    return "dmy";
  } catch {
    return "dmy";
  }
};

export const getLocaleHour12 = (locale?: string) => {
  try {
    return new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hour12 ?? false;
  } catch {
    return false;
  }
};

const DISPLAY_FORMATS: Record<DateOrder, string> = { dmy: "dd/MM/yyyy", mdy: "MM/dd/yyyy", ymd: "yyyy-MM-dd" };
const PLACEHOLDERS: Record<DateOrder, string> = { dmy: "dd/mm/yyyy", mdy: "mm/dd/yyyy", ymd: "yyyy-mm-dd" };
const NUMERIC_PATTERNS: Record<DateOrder, string[]> = {
  dmy: ["d/M/yyyy", "d-M-yyyy", "d.M.yyyy"],
  mdy: ["M/d/yyyy", "M-d-yyyy", "M.d.yyyy"],
  ymd: [],
};
// Unambiguous in every locale, so always accepted.
const ISO_PATTERNS = ["yyyy-M-d", "yyyy/M/d", "yyyy.M.d"];
const NAMED_MONTH_PATTERNS = ["d MMM yyyy", "d MMMM yyyy", "MMM d yyyy", "MMM d, yyyy", "MMMM d yyyy", "MMMM d, yyyy"];

export const getDatePlaceholder = (order: DateOrder) => PLACEHOLDERS[order];

const parseWithPatterns = (text: string, patterns: string[]) => {
  for (const pattern of patterns) {
    const parsed = parse(text, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }
  return null;
};

/**
 * Parses a typed date. Numeric dates must follow the locale's day/month order (no fallback to
 * the other order), and a four digit year is required so partial input like "2-01-01" is not
 * read as the year 2.
 */
export const parseDate = (text: string, order: DateOrder): Date | null => {
  const trimmed = text.trim();
  if (!/\d{4}/.test(trimmed)) return null;
  return parseWithPatterns(trimmed, [...NUMERIC_PATTERNS[order], ...ISO_PATTERNS, ...NAMED_MONTH_PATTERNS]);
};

const TIME_PATTERN = /^(\d{1,2})(?::?(\d{2}))?(?::?(\d{2}))?(am|pm|a|p)?$/;

/** Parses "9", "9:00", "900", "9pm", "9:00 PM", "21:00", "23:59:59". Rejects "21:00am" and "21:61". */
export const parseTime = (text: string): TimeOfDay | null => {
  const match = TIME_PATTERN.exec(text.trim().toLowerCase().replace(/\s+/g, ""));
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const seconds = match[3] ? Number(match[3]) : 0;
  const meridiem = match[4];
  if (minutes > 59 || seconds > 59) return null;

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem.startsWith("p") ? 12 : 0);
  } else if (hours > 23) {
    return null;
  }

  return { hours, minutes, seconds };
};

export type ParsedDateTime = { date: Date; time: TimeOfDay | null };

export const parseDateTime = (text: string, order: DateOrder): ParsedDateTime | null => {
  const normalized = text.trim().replace(/(\d)T(\d)/, "$1 $2");
  if (!normalized) return null;

  const dateOnly = parseDate(normalized, order);
  if (dateOnly) return { date: dateOnly, time: null };

  // The date part can contain spaces ("25 Dec 2026"), so try every split point.
  for (const match of normalized.matchAll(/\s+/g)) {
    const index = match.index ?? 0;
    const date = parseDate(normalized.slice(0, index), order);
    const time = parseTime(normalized.slice(index));
    if (date && time) return { date, time };
  }
  return null;
};

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Parses pasted text that may hold a full date and time: anything parseDateTime accepts, plus ISO
 * timestamps with fractional seconds or a zone ("2026-12-25T10:30:00.000Z"), which are converted to
 * local time.
 */
export const parsePastedDateTime = (text: string, order: DateOrder): ParsedDateTime | null => {
  const local = parseDateTime(text, order);
  if (local) return local;

  const trimmed = text.trim();
  if (!ISO_TIMESTAMP.test(trimmed)) return null;
  const instant = new Date(trimmed);
  if (Number.isNaN(instant.getTime())) return null;
  return { date: startOfDay(instant), time: timeOf(instant) };
};

export const combineDateTime = (date: Date, time: TimeOfDay | null) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time?.hours ?? 0,
    time?.minutes ?? 0,
    time?.seconds ?? 0,
  );

export const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const timeOf = (date: Date): TimeOfDay => ({
  hours: date.getHours(),
  minutes: date.getMinutes(),
  seconds: date.getSeconds(),
});

export const sameTime = (left: TimeOfDay | null | undefined, right: TimeOfDay | null | undefined) =>
  Boolean(left && right) &&
  left?.hours === right?.hours &&
  left?.minutes === right?.minutes &&
  (left?.seconds ?? 0) === (right?.seconds ?? 0);

export const toSeconds = (time: TimeOfDay) => time.hours * 3600 + time.minutes * 60 + (time.seconds ?? 0);

export const formatDate = (date: Date, order: DateOrder) => format(date, DISPLAY_FORMATS[order]);

export type FormatTimeOptions = { hour12: boolean; showSeconds?: boolean };

/** Seconds are shown when `showSeconds` is set or the time has non-zero seconds (e.g. 11:59:59 pm). */
export const formatTime = (time: TimeOfDay, { hour12, showSeconds }: FormatTimeOptions) => {
  const seconds = time.seconds ?? 0;
  const withSeconds = showSeconds || seconds !== 0;
  const pattern = `${hour12 ? "h:mm" : "HH:mm"}${withSeconds ? ":ss" : ""}${hour12 ? " aaa" : ""}`;
  return format(new Date(2000, 0, 1, time.hours, time.minutes, seconds), pattern);
};

/** Canonical 24 hour string used as the TimeInput value: "HH:mm", or "HH:mm:ss" when needed. */
export const timeToValue = (time: TimeOfDay, showSeconds = false) => formatTime(time, { hour12: false, showSeconds });

const STORAGE_PATTERNS = [
  "yyyy-MM-dd HH:mm:ss",
  "yyyy-MM-dd HH:mm",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm",
  "yyyy-MM-dd",
];

/** Default string storage for DateTimeInput: "yyyy-MM-dd", or "yyyy-MM-dd HH:mm[:ss]". */
export const serializeDateValue = (date: Date | null, dateOnly: boolean) => {
  if (!date) return "";
  if (dateOnly) return format(date, "yyyy-MM-dd");
  return format(date, date.getSeconds() ? "yyyy-MM-dd HH:mm:ss" : "yyyy-MM-dd HH:mm");
};

export const deserializeDateValue = (value: string | null | undefined): Date | null => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const parsed = parseWithPatterns(trimmed, STORAGE_PATTERNS);
  if (parsed) return parsed;
  const fallback = new Date(trimmed);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};
